/**
 * § 11 UStG in a list.
 *
 * An incoming invoice missing one of these details costs the input tax deduction, and the
 * Finanzamt finds out years later. Showing it while the document is still on screen is the only
 * moment the supplier can still be asked for a corrected invoice.
 */
import { Check, CircleHelp, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { CheckableDocument, CheckableWorkspace } from "@/lib/at/invoice-check";
import { checkInvoice, SMALL_INVOICE_LIMIT } from "@/lib/at/invoice-check";
import { cn } from "@/lib/utils/cn";

export function InvoiceChecklist({
  document,
  workspace,
}: {
  document: CheckableDocument;
  workspace: CheckableWorkspace;
}) {
  const { t } = useTranslation();
  const result = checkInvoice(document, workspace);

  return (
    <div className="space-y-3" data-testid="invoice-checklist">
      <div>
        <p className="text-sm font-medium">{t("invoiceCheck.title")}</p>
        <p className="text-xs text-muted-foreground">
          {result.simplified
            ? t("invoiceCheck.simplified", { limit: SMALL_INVOICE_LIMIT })
            : t("invoiceCheck.full")}
        </p>
      </div>

      {result.inputTaxAtRisk && (
        <Alert variant="destructive" data-testid="input-tax-warning">
          <AlertTitle>{t("invoiceCheck.atRiskTitle")}</AlertTitle>
          <AlertDescription>{t("invoiceCheck.atRiskText")}</AlertDescription>
        </Alert>
      )}

      <ul className="space-y-1.5">
        {result.requirements.map((requirement) => (
          <li
            key={requirement.id}
            className={cn(
              "flex items-start gap-2 text-sm",
              requirement.ok ? "text-muted-foreground" : "text-foreground",
            )}
            data-testid={`requirement-${requirement.id}`}
          >
            {requirement.manual ? (
              <CircleHelp className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            ) : requirement.ok ? (
              <Check className="mt-0.5 size-4 shrink-0 text-success" />
            ) : (
              <X className="mt-0.5 size-4 shrink-0 text-destructive" />
            )}
            <span>
              {t(`invoiceCheck.requirements.${requirement.id}`)}
              {requirement.manual && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({t("invoiceCheck.checkYourself")})
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
